/**
 * TCO Comparison Component
 *
 * Side-by-side comparison of 2-3 ZFS configurations
 * Shows cost, capacity, reliability, and performance metrics
 * Highlights best values with color coding
 */

import { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import { calculateCapacity } from '../../core/capacity';
import { calculateReliability } from '../../core/reliability';
import { calculatePerformance } from '../../core/performance';
import { calculateEconomics } from '../../core/economics';
import type {
  PoolScenario,
  CapacityResults,
  ReliabilityResults,
  PerformanceResults,
  EconomicsResults,
} from '../../types';

interface TCOComparisonProps {
  scenarios: PoolScenario[];
  onRemove?: (id: string) => void;
}

interface ComparisonRow {
  label: string;
  key: string;
  values: (string | number)[];
  highlight?: 'best' | 'warning' | 'none';
  format?: 'currency' | 'number' | 'percentage' | 'bytes';
}

interface ScenarioComparisonData {
  scenario: PoolScenario;
  capacity: CapacityResults;
  reliability: ReliabilityResults;
  performance: PerformanceResults;
  economics: EconomicsResults;
}

/**
 * Get the index of the best value (lowest for cost, highest for capacity/reliability)
 */
function getBestIndex(values: number[], direction: 'low' | 'high' | 'mid'): number {
  if (direction === 'low') {
    return values.indexOf(Math.min(...values));
  } else if (direction === 'high') {
    return values.indexOf(Math.max(...values));
  } else {
    // For mid (like AFR), find the value closest to 1.5%
    const target = 1.5;
    return values.reduce((bestIdx, val, idx, arr) =>
      Math.abs(val - target) < Math.abs(arr[bestIdx] - target) ? idx : bestIdx
    );
  }
}

export function TCOComparison({ scenarios, onRemove }: TCOComparisonProps) {
  const theme = useTheme();

  // Calculate results for all scenarios
  const comparisonData = useMemo((): ScenarioComparisonData[] => {
    return scenarios.map((scenario) => {
      const capacity = calculateCapacity(
        scenario.topology,
        scenario.drive,
        scenario.zfs,
        scenario.utilization
      );
      const reliability = calculateReliability(
        scenario.topology,
        scenario.drive,
        scenario.utilization,
        capacity.finalUsableTiB
      );
      const performance = calculatePerformance(
        scenario.topology,
        scenario.drive,
        scenario.performance
      );
      const economics = calculateEconomics(
        scenario.topology,
        scenario.drive,
        scenario.economics,
        capacity
      );

      return { scenario, capacity, reliability, performance, economics };
    });
  }, [scenarios]);

  if (comparisonData.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          TCO Comparison
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add scenarios to compare using the preset selector or save custom configurations.
        </Typography>
      </Paper>
    );
  }

  // Extract values for comparison
  const hardwareCosts = comparisonData.map((d) => d.economics.hardwareCost);
  const costPerTiB = comparisonData.map((d) => d.economics.costPerUsableTB);
  const annualPowerCost = comparisonData.map((d) => d.economics.annualPowerCost);
  const fiveYearTCO = comparisonData.map((d) => d.economics.tcoProjection[4]?.cumulativeCost || 0);
  const usableTiB = comparisonData.map((d) => d.capacity.finalUsableTiB);
  const totalDrives = comparisonData.map((d) => d.economics.totalDrives);
  const totalWatts = comparisonData.map((d) => d.economics.totalWattsIdle);
  const mttpYears = comparisonData.map((d) => d.reliability.mttpYears);
  const dataLossProb = comparisonData.map((d) => d.reliability.totalDataLossProbability * 100);
  const resilverTime = comparisonData.map((d) => d.reliability.resilverTimeHours);
  const randomIOPS = comparisonData.map((d) => d.performance.randomReadIOPS);
  const sequentialMBps = comparisonData.map((d) => d.performance.sequentialReadMBps);

  // Find best values
  const bestHardwareCost = getBestIndex(hardwareCosts, 'low');
  const bestCostPerTiB = getBestIndex(costPerTiB, 'low');
  const bestAnnualPower = getBestIndex(annualPowerCost, 'low');
  const bestTCO = getBestIndex(fiveYearTCO, 'low');
  const bestCapacity = getBestIndex(usableTiB, 'high');
  const bestMTTP = getBestIndex(mttpYears, 'high');
  const bestReliability = getBestIndex(dataLossProb, 'low');
  const bestResilverTime = getBestIndex(resilverTime, 'low');
  const bestIOPS = getBestIndex(randomIOPS, 'high');
  const bestThroughput = getBestIndex(sequentialMBps, 'high');

  // Build comparison rows
  const rows: ComparisonRow[] = [
    {
      label: 'Hardware Cost',
      key: 'hardwareCost',
      values: hardwareCosts,
      format: 'currency',
    },
    {
      label: 'Cost per TiB',
      key: 'costPerTiB',
      values: costPerTiB,
      format: 'currency',
    },
    {
      label: 'Annual Power Cost',
      key: 'annualPowerCost',
      values: annualPowerCost,
      format: 'currency',
    },
    {
      label: '5-Year TCO',
      key: 'fiveYearTCO',
      values: fiveYearTCO,
      format: 'currency',
    },
    {
      label: 'Usable Capacity',
      key: 'usableTiB',
      values: usableTiB,
      format: 'bytes',
    },
    {
      label: 'Total Drives',
      key: 'totalDrives',
      values: totalDrives,
      format: 'number',
    },
    {
      label: 'Idle Power',
      key: 'totalWatts',
      values: totalWatts,
      format: 'number',
    },
    {
      label: 'MTTP (Years)',
      key: 'mttpYears',
      values: mttpYears,
      format: 'number',
    },
    {
      label: 'Data Loss Risk',
      key: 'dataLossProb',
      values: dataLossProb,
      format: 'percentage',
    },
    {
      label: 'Resilver Time',
      key: 'resilverTime',
      values: resilverTime,
      format: 'number',
    },
    {
      label: 'Random Read IOPS',
      key: 'randomIOPS',
      values: randomIOPS,
      format: 'number',
    },
    {
      label: 'Sequential Read (MB/s)',
      key: 'sequentialMBps',
      values: sequentialMBps,
      format: 'number',
    },
  ];

  // Helper to format values
  const formatValue = (value: string | number, format?: string): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;

    if (format === 'currency') {
      return `$${num.toFixed(2)}`;
    } else if (format === 'percentage') {
      return `${num.toFixed(3)}%`;
    } else if (format === 'bytes') {
      return `${num.toFixed(2)} TiB`;
    } else {
      return num.toFixed(num > 1000 ? 0 : 2);
    }
  };

  // Helper to get cell background color
  const getCellColor = (rowIndex: number, colIndex: number): string => {
    const row = rows[rowIndex];
    const bestIndices: Record<string, number> = {
      hardwareCost: bestHardwareCost,
      costPerTiB: bestCostPerTiB,
      annualPowerCost: bestAnnualPower,
      fiveYearTCO: bestTCO,
      usableTiB: bestCapacity,
      mttpYears: bestMTTP,
      dataLossProb: bestReliability,
      resilverTime: bestResilverTime,
      randomIOPS: bestIOPS,
      sequentialMBps: bestThroughput,
    };

    if (bestIndices[row.key] === colIndex) {
      return theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)';
    }

    // Warning for high AFR or long resilver
    if (row.key === 'dataLossProb' && typeof row.values[colIndex] === 'number' && row.values[colIndex] > 5) {
      return theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)';
    }
    if (row.key === 'resilverTime' && typeof row.values[colIndex] === 'number' && row.values[colIndex] > 72) {
      return theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)';
    }

    return 'transparent';
  };

  // Summary metrics
  const summaryChips = [
    { label: 'Best Value', value: `$${Math.min(...hardwareCosts).toFixed(0)}`, color: 'success' },
    { label: 'Most Capacity', value: `${Math.max(...usableTiB).toFixed(1)} TiB`, color: 'info' },
    { label: 'Most Reliable', value: `${(Math.min(...dataLossProb) * 100).toFixed(3)}% risk`, color: 'info' },
    { label: 'Best TCO', value: `$${Math.min(...fiveYearTCO).toFixed(0)}`, color: 'success' },
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">TCO Comparison</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {summaryChips.map((chip) => (
            <Chip
              key={chip.label}
              label={`${chip.label}: ${chip.value}`}
              color={chip.color as any}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      </Stack>

      {/* Scenario Headers */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {comparisonData.map((data, idx) => (
          <Box
            key={data.scenario.id}
            sx={{
              flex: 1,
              minWidth: 200,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              {data.scenario.name || `Configuration ${idx + 1}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.scenario.topology.raidLevel.toUpperCase()} • {data.economics.totalDrives} drives •{' '}
              {data.scenario.drive.driveSizeTB}TB
            </Typography>
            {data.scenario.drive.driveAge !== undefined && data.scenario.drive.driveAge > 0 && (
              <Chip size="small" label={`${data.scenario.drive.driveAge}y old`} sx={{ mt: 0.5 }} />
            )}
            {onRemove && (
              <Button
                size="small"
                color="error"
                onClick={() => onRemove(data.scenario.id)}
                sx={{ mt: 1, fontSize: '0.7rem' }}
              >
                Remove
              </Button>
            )}
          </Box>
        ))}
      </Box>

      {/* Comparison Table */}
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              {comparisonData.map((data, idx) => (
                <TableCell key={data.scenario.id} align="right">
                  Config {idx + 1}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={row.key}>
                <TableCell component="th" scope="row">
                  <Typography variant="body2" fontWeight="medium">
                    {row.label}
                  </Typography>
                </TableCell>
                {row.values.map((value, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align="right"
                    sx={{
                      backgroundColor: getCellColor(rowIndex, colIndex),
                      fontWeight: getCellColor(rowIndex, colIndex) !== 'transparent' ? 'bold' : 'normal',
                    }}
                  >
                    {formatValue(value, row.format)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Legend:</strong>
          <span style={{ marginLeft: 16, display: 'inline-block', width: 12, height: 12, backgroundColor: 'rgba(76, 175, 80, 0.3)', borderRadius: '50%' }}></span>
          {' '}Best value
          <span style={{ marginLeft: 8, display: 'inline-block', width: 12, height: 12, backgroundColor: 'rgba(244, 67, 54, 0.3)', borderRadius: '50%' }}></span>
          {' '}High risk
          <span style={{ marginLeft: 8, display: 'inline-block', width: 12, height: 12, backgroundColor: 'rgba(255, 152, 0, 0.3)', borderRadius: '50%' }}></span>
          {' '}Warning
        </Typography>
      </Box>

      {/* Recommendations */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Insights
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          {bestHardwareCost !== bestCapacity && (
            <Box sx={{ mb: 0.5 }}>
              • Lowest upfront cost: <strong>Config {bestHardwareCost + 1}</strong> (${hardwareCosts[bestHardwareCost].toFixed(0)})
            </Box>
          )}
          {bestCostPerTiB !== bestCapacity && (
            <Box sx={{ mb: 0.5 }}>
              • Best cost per TiB: <strong>Config {bestCostPerTiB + 1}</strong> (${costPerTiB[bestCostPerTiB].toFixed(2)}/TiB)
            </Box>
          )}
          {bestMTTP !== bestTCO && (
            <Box sx={{ mb: 0.5 }}>
              • Most reliable: <strong>Config {bestMTTP + 1}</strong> ({mttpYears[bestMTTP] > 1000 ? '1000+ years' : `${mttpYears[bestMTTP].toFixed(0)} years`})
            </Box>
          )}
          {bestIOPS !== bestCapacity && (
            <Box sx={{ mb: 0.5 }}>
              • Best performance: <strong>Config {bestIOPS + 1}</strong> ({randomIOPS[bestIOPS].toFixed(0)} IOPS)
            </Box>
          )}
        </Typography>
      </Box>
    </Paper>
  );
}
